import { Column } from '@tanstack/react-table';
import { TextInput } from '@carbon/react';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { DataTableColumnMeta } from './types';
import styles from './header-range-filter.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface HeaderRangeFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
}

// ─────────────────────────────────────
// Numeric text helpers
// ─────────────────────────────────────

// Partial numeric entry: optional leading '-', any digits, at most one '.',
// any digits. Deliberately permissive so intermediate states the user must
// pass THROUGH while typing a valid number are allowed: '', '-', '.', '.0',
// '0.', '-.5'. Rejects letters, multiple dots/minuses, exponents.
const PARTIAL_NUMBER = /^-?\d*\.?\d*$/;

/**
 * Parse the raw input text to a number, or null when it isn't (yet) a number.
 * @param {string} text - Raw input string from the field
 * @return {number | null} Parsed number, or null for empty/partial/invalid input
 */
function textToNumber(text: string): number | null {
  const trimmed = text.trim();
  // Empty or a lone sign/dot is not a committable number.
  if (
    trimmed === '' ||
    trimmed === '-' ||
    trimmed === '.' ||
    trimmed === '-.'
  ) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Render a committed numeric filter value back to input text.
 * @param {number | null} value - The committed filter value for one side
 * @return {string} Text to show in the input ('' when null)
 */
function numberToText(value: number | null): string {
  return value === null ? '' : String(value);
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Inline numeric range filter for the table header filter row.
 * Displays two compact number inputs for min and max values.
 * Includes debouncing to prevent excessive filtering during input.
 *
 * The inputs are `type="text"` (with `inputMode="decimal"`), not
 * `type="number"`, and hold the RAW typed string in local state. A controlled
 * `type="number"` input can't represent the intermediate states a user types
 * through — a bare leading '.', a trailing '.' (`0.`), or a lone '-' — because
 * the browser reports an empty `.value` for them, so round-tripping through
 * `Number().toString()` on each keystroke silently drops those characters
 * (e.g. `.01` / `0.01` become impossible to enter). Keeping the raw string and
 * only parsing to a number when committing the filter fixes that.
 * @param {HeaderRangeFilterProps} props - Component props
 * @param {Column} props.column - TanStack Table column instance
 * @return {JSX.Element} The header range filter element
 */
export function HeaderRangeFilter<TData, TValue>({
  column,
}: HeaderRangeFilterProps<TData, TValue>) {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const rawFilterValue = column.getFilterValue() as
    [number | null, number | null] | undefined;
  const filterValue = useMemo<[number | null, number | null]>(
    () => rawFilterValue ?? [null, null],
    [rawFilterValue]
  );
  const [minText, setMinText] = useState(() => numberToText(filterValue[0]));
  const [maxText, setMaxText] = useState(() => numberToText(filterValue[1]));

  const debouncedSetFilter = useMemo(
    () =>
      debounce((minStr: string, maxStr: string) => {
        const min = textToNumber(minStr);
        const max = textToNumber(maxStr);
        const isEmpty = min === null && max === null;
        column.setFilterValue(isEmpty ? undefined : [min, max]);
      }, 300),
    [column]
  );

  // Cancel debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  // Sync from external filter changes (Reset, URL rehydrate) without clobbering
  // the raw text the user is mid-typing: only overwrite a side when its parsed
  // value no longer matches the incoming filter value. This keeps a partial
  // entry like '.01' or '0.' intact when the debounced commit echoes back the
  // equal numeric value a render later.
  useEffect(() => {
    if (textToNumber(minText) !== filterValue[0]) {
      setMinText(numberToText(filterValue[0]));
    }
    if (textToNumber(maxText) !== filterValue[1]) {
      setMaxText(numberToText(filterValue[1]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (!PARTIAL_NUMBER.test(next)) return; // reject non-numeric keystrokes
    setMinText(next);
    debouncedSetFilter(next, maxText);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (!PARTIAL_NUMBER.test(next)) return; // reject non-numeric keystrokes
    setMaxText(next);
    debouncedSetFilter(minText, next);
  };

  return (
    <div className={styles.rangeFilter}>
      <TextInput
        id={`filter-${column.id}-min`}
        labelText=""
        hideLabel
        size="sm"
        type="text"
        inputMode="decimal"
        autoComplete={meta?.disableFilterAutocomplete ? 'off' : undefined}
        value={minText}
        onChange={handleMinChange}
        placeholder="Min"
        className={styles.input}
      />
      <TextInput
        id={`filter-${column.id}-max`}
        labelText=""
        hideLabel
        size="sm"
        type="text"
        inputMode="decimal"
        autoComplete={meta?.disableFilterAutocomplete ? 'off' : undefined}
        value={maxText}
        onChange={handleMaxChange}
        placeholder="Max"
        className={styles.input}
      />
    </div>
  );
}
